import { useState, useEffect } from "react";
import { Helmet } from "react-helmet-async";
import { Link } from "react-router-dom";
import AppNav from "../components/appNav";
import Footer from "../components/footer";
import { useTranslation } from "react-i18next";
import { trackMVPBuild } from "../firebase";

const Section = ({
  num,
  title,
  children,
}: {
  num: string;
  title?: string;
  children: React.ReactNode;
}) => (
  <section className="swiss-section">
    <div className="swiss-grid">
      <div className="col-span-12 lg:col-span-2 mb-6 lg:mb-0">
        <span className="swiss-num">{num}</span>
      </div>
      <div className="col-span-12 lg:col-span-10">
        {title && (
          <h2 className="text-h2 font-bold tracking-tight text-ink mb-7 max-w-[22ch]">{title}</h2>
        )}
        {children}
      </div>
    </div>
  </section>
);

const MVPBuildPage = () => {
  const { t } = useTranslation();
  const [showMethodologyModal, setShowMethodologyModal] = useState(false);

  useEffect(() => {
    trackMVPBuild.pageView();
  }, []);

  const handleMethodologyOpen = () => {
    trackMVPBuild.methodologyOpen();
    setShowMethodologyModal(true);
  };

  const handleMethodologyClose = () => {
    trackMVPBuild.methodologyClose();
    setShowMethodologyModal(false);
  };

  return (
    <>
      <Helmet>
        <title>KODE MVP Builder Program | 8 Weeks: Build Your MVP + Learn to Evolve It</title>
        <meta name="description" content="8-week intensive program. We build your MVP in 4 weeks, then teach you to evolve it. January 2025 cohort." />
      </Helmet>

      <div className="min-h-screen bg-paper text-ink">
        <header className="sticky top-0 z-50 bg-paper/95 backdrop-blur-[2px] border-b border-ink-14">
          <AppNav />
        </header>

        <main>
          {/* 01 / Hero */}
          <section className="swiss-section !border-t-0 pt-7 lg:pt-9">
            <div className="swiss-grid">
              <div className="col-span-12 lg:col-span-2 mb-6 lg:mb-0">
                <span className="swiss-num">01 / MVP</span>
              </div>
              <div className="col-span-12 lg:col-span-10">
                <h1 className="text-[clamp(2.5rem,5.5vw,5.5rem)] font-black tracking-tightest leading-[0.95] text-ink max-w-[16ch] mb-7">
                  {t('mvpBuilderProgram.hero.title')}
                </h1>
                <p className="text-body-lg text-ink-70 max-w-prose mb-7">
                  {t('mvpBuilderProgram.hero.subtitle')}
                </p>
                <p className="text-body text-ink-70 max-w-prose mb-7">
                  {t('mvpBuilderProgram.hero.description')}
                </p>

                <div className="flex flex-wrap gap-3 mb-7">
                  <a
                    href="https://wa.me/34636349614?text=Hi!%20I'm%20interested%20in%20the%20MVP%20Builder%20Program."
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={() => trackMVPBuild.whatsappClick()}
                    className="btn btn-primary"
                  >
                    {t('mvpBuilderProgram.cta.whatsappButton') || "Chat now"}
                  </a>
                  <a
                    href="https://calendly.com/bogowi/kode"
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={() => trackMVPBuild.bookCallClick()}
                    className="btn btn-secondary"
                  >
                    {t('mvpBuilderProgram.cta.bookCallButton') || "Schedule a call"}
                  </a>
                  <button onClick={handleMethodologyOpen} className="btn btn-secondary">
                    {t('mvpBuilderProgram.hero.methodologyBadge')}
                  </button>
                </div>

                <a
                  href="#grant-program"
                  className="inline-flex items-center gap-3 swiss-caption link-underline text-ink"
                >
                  <span className="text-accent">NEW</span>
                  {t('mvpBuilderProgram.grant.title')} — {t('mvpBuilderProgram.grant.tiers.strong.youPay')}
                </a>
              </div>
            </div>
          </section>

          {/* 02 / Two parts */}
          <Section num={`02 / ${t('Parts')}`}>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {(["part1", "part2"] as const).map((part, i) => (
                <div key={part} className="border-t border-ink pt-4">
                  <span className="swiss-caption mb-7 block">
                    {String(i + 1).padStart(2, "0")} / {t(`mvpBuilderProgram.parts.${part}.subtitle`)}
                  </span>
                  <h3 className="text-h3 font-medium tracking-tight text-ink mb-5">
                    {t(`mvpBuilderProgram.parts.${part}.title`)}
                  </h3>
                  <ul className="flex flex-col gap-2 text-body text-ink-70 mb-5">
                    {["item1", "item2", "item3", "item4"].map((k) => (
                      <li key={k} className="flex gap-3">
                        <span className="text-ink-50">—</span>
                        <span>{t(`mvpBuilderProgram.parts.${part}.${k}`)}</span>
                      </li>
                    ))}
                  </ul>
                  <p className="text-body font-medium text-ink border-t border-ink-14 pt-4">
                    {t(`mvpBuilderProgram.parts.${part}.result`)}
                  </p>
                </div>
              ))}
            </div>
          </Section>

          {/* 03 / Who is this for */}
          <Section num={`03 / ${t('Fit')}`} title={t('mvpBuilderProgram.whoIsItFor.title')}>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {(["perfectFor", "notFor"] as const).map((k) => (
                <div key={k} className="border-t border-ink pt-4">
                  <h3 className="text-h4 font-medium tracking-tight text-ink mb-5">
                    {t(`mvpBuilderProgram.whoIsItFor.${k}.title`)}
                  </h3>
                  <ul className="flex flex-col gap-2 text-body text-ink-70">
                    {(k === "perfectFor" ? [1, 2, 3, 4, 5] : [1, 2, 3, 4]).map((i) => (
                      <li key={i} className="flex gap-3">
                        <span className="text-ink-50">{k === "perfectFor" ? "+" : "—"}</span>
                        <span>{t(`mvpBuilderProgram.whoIsItFor.${k}.item${i}`)}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          </Section>

          {/* 04 / Weekly */}
          <Section num={`04 / ${t('Schedule')}`} title={t('mvpBuilderProgram.weeklyBreakdown.title')}>
            {[
              { label: t('mvpBuilderProgram.weeklyBreakdown.buildingPhase'), weeks: [1, 2, 3, 4] },
              { label: t('mvpBuilderProgram.weeklyBreakdown.trainingPhase'), weeks: [5, 6, 7, 8] },
            ].map((phase) => (
              <div key={phase.label} className="mb-9 last:mb-0">
                <span className="swiss-caption block mb-5">{phase.label}</span>
                <ul className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                  {phase.weeks.map((week) => (
                    <li key={week} className="border-t border-ink pt-4">
                      <span className="swiss-num mb-5 block">{String(week).padStart(2, "0")}</span>
                      <h4 className="text-h4 font-medium tracking-tight text-ink mb-3">
                        {t(`mvpBuilderProgram.weeklyBreakdown.week${week}.title`)}
                      </h4>
                      <p className="text-body text-ink-70">
                        {t(`mvpBuilderProgram.weeklyBreakdown.week${week}.description`)}
                      </p>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </Section>

          {/* 05 / Cohort */}
          <Section num={`05 / ${t('Cohort')}`} title={t('mvpBuilderProgram.firstCohort.title')}>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-7">
              {(["details", "includes"] as const).map((k) => (
                <div key={k} className="border-t border-ink pt-4">
                  <h3 className="text-h4 font-medium tracking-tight text-ink mb-5">
                    {t(`mvpBuilderProgram.firstCohort.${k}.title`)}
                  </h3>
                  <ul className="flex flex-col gap-3 text-body text-ink-70">
                    {k === "details" ? (
                      <>
                        <li><span className="swiss-caption text-ink mr-2">{t('mvpBuilderProgram.firstCohort.details.dates')}</span> 13 Jan – 8 Mar 2025</li>
                        <li><span className="swiss-caption text-ink mr-2">{t('mvpBuilderProgram.firstCohort.details.participants')}</span> {t('mvpBuilderProgram.firstCohort.details.maxFounders')}</li>
                        <li><span className="swiss-caption text-ink mr-2">{t('mvpBuilderProgram.firstCohort.details.modality')}</span> {t('mvpBuilderProgram.firstCohort.details.modalityDesc')}</li>
                        <li><span className="swiss-caption text-ink mr-2">{t('mvpBuilderProgram.firstCohort.details.sessions')}</span> {t('mvpBuilderProgram.firstCohort.details.sessionsDesc')}</li>
                      </>
                    ) : (
                      [1, 2, 3, 4, 5].map((i) => (
                        <li key={i} className="flex gap-3">
                          <span className="text-ink-50">—</span>
                          <span>{t(`mvpBuilderProgram.firstCohort.includes.item${i}`)}</span>
                        </li>
                      ))
                    )}
                  </ul>
                </div>
              ))}
            </div>
            <div className="border-t border-ink pt-4 flex flex-wrap items-baseline gap-6">
              <p className="text-[clamp(2.5rem,5vw,4rem)] font-black tracking-tighter leading-none text-ink">
                {t('mvpBuilderProgram.firstCohort.price')}
              </p>
              <p className="text-body text-ink-70">{t('mvpBuilderProgram.firstCohort.priceNote')}</p>
              <p className="swiss-caption text-accent">
                {t('mvpBuilderProgram.firstCohort.spotsAvailable')}
              </p>
            </div>
          </Section>

          {/* 06 / Grant */}
          <section id="grant-program" className="swiss-section scroll-mt-24">
            <div className="swiss-grid">
              <div className="col-span-12 lg:col-span-2 mb-6 lg:mb-0">
                <span className="swiss-num">06 / {t('Grant')}</span>
              </div>
              <div className="col-span-12 lg:col-span-10">
                <span className="swiss-caption text-accent block mb-5">NEW</span>
                <h2 className="text-h2 font-bold tracking-tight text-ink mb-5 max-w-[22ch]">
                  {t('mvpBuilderProgram.grant.title')}
                </h2>
                <p className="text-body-lg text-ink-70 max-w-prose mb-9">
                  {t('mvpBuilderProgram.grant.subtitle')}
                </p>

                <h3 className="text-h4 font-medium tracking-tight text-ink mb-5">
                  {t('mvpBuilderProgram.grant.howItWorks.title')}
                </h3>
                <ol className="grid grid-cols-2 md:grid-cols-4 gap-6 mb-9 border-b border-ink-14 pb-9">
                  {[1, 2, 3, 4].map((step) => (
                    <li key={step} className="border-t border-ink pt-4">
                      <span className="swiss-num mb-3 block">
                        {String(step).padStart(2, "0")}
                      </span>
                      <p className="text-body text-ink-70">
                        {t(`mvpBuilderProgram.grant.howItWorks.step${step}`)}
                      </p>
                    </li>
                  ))}
                </ol>

                <h3 className="text-h4 font-medium tracking-tight text-ink mb-5">
                  {t('mvpBuilderProgram.grant.tiers.title')}
                </h3>
                <ul className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-9">
                  {(["standard", "strong"] as const).map((tier) => (
                    <li key={tier} className="border-t border-ink pt-4">
                      <div className="flex items-baseline justify-between mb-5">
                        <h4 className="text-h4 font-medium tracking-tight text-ink">
                          {t(`mvpBuilderProgram.grant.tiers.${tier}.title`)}
                        </h4>
                        <span className="swiss-caption">
                          {t(`mvpBuilderProgram.grant.tiers.${tier}.coverage`)}
                        </span>
                      </div>
                      <p className="text-h3 font-bold tracking-tight text-ink mb-2">
                        {t(`mvpBuilderProgram.grant.tiers.${tier}.youPay`)}
                      </p>
                      <p className="swiss-caption mb-5">
                        {t(`mvpBuilderProgram.grant.tiers.${tier}.equity`)}
                      </p>
                      <p className="text-body text-ink-70 max-w-measure">
                        {t(`mvpBuilderProgram.grant.tiers.${tier}.description`)}
                      </p>
                      {tier === "strong" && (
                        <p className="swiss-caption text-accent mt-3">BEST VALUE</p>
                      )}
                    </li>
                  ))}
                </ul>

                <div>
                  <p className="text-body-lg text-ink mb-5">
                    {t('mvpBuilderProgram.grant.cta.title')}
                  </p>
                  <Link to="/grant-application" className="btn btn-primary">
                    {t('mvpBuilderProgram.grant.cta.button')}
                  </Link>
                  <p className="swiss-caption mt-4">{t('mvpBuilderProgram.grant.cta.note')}</p>
                </div>
              </div>
            </div>
          </section>

          {/* 07 / MVP profile */}
          <Section num={`07 / ${t('Scope')}`} title={t('mvpBuilderProgram.mvpProfile.title')}>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="border-t border-ink pt-4">
                <h3 className="text-h4 font-medium tracking-tight text-ink mb-5">
                  {t('mvpBuilderProgram.mvpProfile.productTypes.title')}
                </h3>
                <ul className="flex flex-col gap-2 text-body text-ink-70">
                  {["saas", "marketplace", "ecommerce", "management", "apis"].map((k) => (
                    <li key={k} className="flex gap-3">
                      <span className="text-ink-50">—</span>
                      <span>{t(`mvpBuilderProgram.mvpProfile.productTypes.${k}`)}</span>
                    </li>
                  ))}
                </ul>
              </div>
              <div className="border-t border-ink pt-4">
                <h3 className="text-h4 font-medium tracking-tight text-ink mb-5">
                  {t('mvpBuilderProgram.mvpProfile.techStack.title')}
                </h3>
                <ul className="flex flex-col gap-2 text-body text-ink-70">
                  {[
                    ["Frontend", "React, Next.js, Vue"],
                    ["Backend", "Node.js, Python"],
                    ["Database", "PostgreSQL, MongoDB"],
                    ["Hosting", "Firebase, Supabase, Vercel"],
                    ["Payments", "Stripe, PayPal"],
                    ["Auth", "Email, Google, Social"],
                  ].map(([label, value]) => (
                    <li key={label} className="flex gap-3">
                      <span className="swiss-caption text-ink w-24 flex-none">{label}</span>
                      <span className="flex-1">{value}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </Section>

          {/* 08 / Why it works */}
          <Section num={`08 / ${t('Method')}`} title={t('mvpBuilderProgram.whyItWorks.title')}>
            <ul className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              {(["buildTogether", "realCode", "contextualLearning", "continuousRelationship"] as const).map((k) => (
                <li key={k} className="border-t border-ink pt-4">
                  <h4 className="text-h4 font-medium tracking-tight text-ink mb-3">
                    {t(`mvpBuilderProgram.whyItWorks.${k}.title`)}
                  </h4>
                  <p className="text-body text-ink-70">
                    {t(`mvpBuilderProgram.whyItWorks.${k}.description`)}
                  </p>
                </li>
              ))}
            </ul>
          </Section>

          {/* 09 / CTA */}
          <section className="swiss-section">
            <div className="swiss-grid">
              <div className="col-span-12 lg:col-span-2 mb-6 lg:mb-0">
                <span className="swiss-num">09 / {t('Next')}</span>
              </div>
              <div className="col-span-12 lg:col-span-10">
                <h2 className="text-h2 font-bold tracking-tight text-ink mb-5 max-w-[22ch]">
                  {t('mvpBuilderProgram.cta.title')}
                </h2>
                <p className="text-body-lg text-ink-70 max-w-prose mb-7">
                  {t('mvpBuilderProgram.cta.description')}
                </p>
                <div className="flex flex-wrap gap-3">
                  <a
                    href="https://wa.me/34636349614"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="btn btn-primary"
                  >
                    {t('mvpBuilderProgram.cta.button')}
                  </a>
                  <a href="mailto:hello@kode.zone" className="btn btn-secondary">
                    {t('mvpBuilderProgram.cta.email')}
                  </a>
                </div>
                <p className="swiss-caption mt-7">{t('mvpBuilderProgram.cta.deadline')}</p>
              </div>
            </div>
          </section>

          <Footer />
        </main>

        {showMethodologyModal && (
          <div
            role="dialog"
            aria-modal="true"
            className="fixed inset-0 z-[100] flex items-start justify-center p-4 bg-ink/40"
            onClick={handleMethodologyClose}
            onKeyDown={(e) => e.key === "Escape" && handleMethodologyClose()}
          >
            <div
              className="bg-paper-pure border border-ink max-w-2xl w-full max-h-[90vh] overflow-y-auto my-10 p-8"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex justify-between items-start mb-7 border-b border-ink-14 pb-5">
                <h2 className="text-h3 font-medium tracking-tight text-ink">
                  {t('mvpBuilderProgram.methodology.title')}
                </h2>
                <button
                  onClick={handleMethodologyClose}
                  className="w-10 h-10 border border-ink-14 inline-flex items-center justify-center text-ink"
                  aria-label="Close"
                >
                  ×
                </button>
              </div>

              <div className="mb-7">
                <div className="flex items-baseline gap-3 mb-3">
                  <h3 className="text-h4 font-medium tracking-tight text-ink">
                    {t('mvpBuilderProgram.methodology.specDev.title')}
                  </h3>
                  <span className="swiss-caption text-accent">
                    {t('mvpBuilderProgram.methodology.specDev.badge')}
                  </span>
                </div>
                <p className="text-body text-ink-70 mb-4">
                  {t('mvpBuilderProgram.methodology.specDev.description')}
                </p>
                <ul className="flex flex-col gap-2 text-body text-ink-70">
                  {["point1", "point2", "point3", "point4"].map((p) => (
                    <li key={p} className="flex gap-3">
                      <span className="text-ink">+</span>
                      <span>{t(`mvpBuilderProgram.methodology.specDev.${p}`)}</span>
                    </li>
                  ))}
                </ul>
              </div>

              <div className="border-t border-ink-14 pt-7">
                <div className="flex items-baseline gap-3 mb-3">
                  <h3 className="text-h4 font-medium tracking-tight text-ink">
                    {t('mvpBuilderProgram.methodology.vibeCoding.title')}
                  </h3>
                  <span className="swiss-caption text-signal">
                    {t('mvpBuilderProgram.methodology.vibeCoding.badge')}
                  </span>
                </div>
                <p className="text-body text-ink-70 mb-4">
                  {t('mvpBuilderProgram.methodology.vibeCoding.description')}
                </p>
                <ul className="flex flex-col gap-2 text-body text-ink-70">
                  {["point1", "point2", "point3", "point4"].map((p) => (
                    <li key={p} className="flex gap-3">
                      <span className="text-ink-50">—</span>
                      <span>{t(`mvpBuilderProgram.methodology.vibeCoding.${p}`)}</span>
                    </li>
                  ))}
                </ul>
              </div>

              <p className="swiss-caption mt-7 border-t border-ink-14 pt-5">
                {t('mvpBuilderProgram.methodology.bottomNote')}
              </p>
            </div>
          </div>
        )}
      </div>
    </>
  );
};

export default MVPBuildPage;
